// packages/web/src/components/account/auth-change-password.tsx
/**
 * Change password form — opened from the account drawer (not the login flow).
 * Requires current password + new password (8+ chars) + confirmation.
 */

import { useState } from 'react';
import { useT } from '../../i18n/use-t.js';
import { authApi } from '../../api/auth.js';
import { IngestApiError } from '../../lib/api-client.js';
import { useNotify } from '../../lib/hooks/use-notify.js';

interface Props {
  onClose: () => void;
}

export function AuthChangePasswordForm({ onClose }: Props) {
  const t = useT();
  const notify = useNotify();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError(null);
    if (next.length < 8) {
      setError(t('auth.weakPassword'));
      return;
    }
    if (next !== confirm) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    setLoading(true);
    try {
      await authApi.changePassword(current, next);
      setDone(true);
      // 19BN Phase D D6: notify the message center on password change.
      notify({
        source: 'system',
        level: 'info',
        title: '密码已修改',
        persistent: false,
      });
      setTimeout(onClose, 1200);
    } catch (err) {
      if (err instanceof IngestApiError && err.status === 401) {
        setError(t('auth.wrongCurrentPassword'));
      } else {
        setError(err instanceof Error ? err.message : 'Change failed');
      }
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="auth-body" style={{ textAlign: 'center', padding: 'var(--space-4xl) var(--space-3xl)' }}>
        <div className="auth-magic-sent-icon" style={{ background: 'var(--color-success-soft)', color: 'var(--color-success-deep)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-ink)', margin: 'var(--space-xl) 0 0' }}>
          {t('auth.passwordChanged')}
        </h3>
      </div>
    );
  }

  return (
    <>
      <div className="auth-head">
        <span className="auth-head-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
            <rect width="18" height="11" x="3" y="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>
        <h2>{t('auth.changePassword')}</h2>
      </div>
      <form className="auth-body" onSubmit={submit}>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-cp-current">{t('auth.currentPassword')}</label>
          <input
            id="auth-cp-current"
            className={`auth-field-input${error === t('auth.wrongCurrentPassword') ? ' has-error' : ''}`}
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-cp-new">{t('auth.newPassword')}</label>
          <input
            id="auth-cp-new"
            className={`auth-field-input${error === t('auth.weakPassword') ? ' has-error' : ''}`}
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
          />
        </div>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-cp-confirm">{t('auth.confirmPassword')}</label>
          <input
            id="auth-cp-confirm"
            className={`auth-field-input${error === t('auth.passwordMismatch') ? ' has-error' : ''}`}
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>
        {error && (
          <div className="auth-field-error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}
        <button type="submit" className={`auth-btn-primary${loading ? ' is-loading' : ''}`} disabled={loading}>
          {t('auth.changePassword')}
        </button>
      </form>
    </>
  );
}
